/**
 * Report focus SSoT.
 *
 * One atom captures where the radiologist's attention is in the report
 * editor. Every feature that needs to know "which chip / where in the
 * doc" (dictation routing, voice-adjust target, InlineChipCard visibility,
 * Tab navigation cursor, [data-chip-focused] styling) reads this atom.
 *
 * Four disjoint modes:
 *   - none           — caret is not in the editor.
 *   - chip-focused   — Tab landed on a chip; NodeSelection on the field,
 *                       chip outline visible, keyboard shortcuts (E/M/⌫/
 *                       Enter/Esc) fire against this chip.
 *   - caret-in-chip  — DOM caret inside a chip's inner contenteditable
 *                       (mouse click into the chip text); ordinary
 *                       contenteditable typing.
 *   - caret-in-prose — PM caret between chips (free prose).
 *
 * Writers (single-writer per signal):
 *   - marks.ts navigate(): chip-focused
 *   - ChipView inner onFocus: caret-in-chip
 *   - PrototypeReport editor.on("selectionUpdate"): caret-in-prose
 *   - PrototypeReport editor.on("blur") + capture-phase focusout: none
 *
 * sectionKind is the enclosing organSubsection.attrs.kind ("liver",
 * "adrenal", ...) or "impression" for impressionSection content. Used as
 * the anatomical anchor by AI prefill / voice-adjust — replaces the
 * LLM/keyword classifier that was sending findings to the wrong section.
 *
 * chipId corresponds to FieldNode.attrs.id (the data-id attribute on the
 * rendered span). The reporter toggles [data-chip-focused="true"] on the
 * matching DOM node when mode === "chip-focused".
 */

import { atom } from "jotai";

export type ReportFocus =
  | { mode: "none" }
  | { mode: "chip-focused"; chipId: string; sectionKind: string | null }
  | { mode: "caret-in-chip"; chipId: string; sectionKind: string | null }
  | { mode: "caret-in-prose"; pos: number; sectionKind: string | null };

export const reportFocusAtom = atom<ReportFocus>({ mode: "none" });

/**
 * In-flight dictation transcript scoped to a chip. Chips are atomic PM
 * nodes (text lives on attrs, no PM positions exist inside), so the
 * widget-decoration ghost plugin can only render at the field boundary.
 * For chip-context dictation, write here instead; ChipView reads this
 * atom and splices the in-progress transcript into the inner
 * contenteditable at the caret offset captured at gesture-start.
 *
 * `offset` is the caret position within the chip's existing text at the
 * moment the rad pressed backtick (read from window.getSelection() —
 * the browser's intrinsic SSoT for sub-position inside a contenteditable;
 * we don't shadow it into a Jotai atom because that would mean writing
 * on every selectionchange, and we only need the value once per gesture).
 * If null, the ghost renders at end-of-text (e.g., when the chip was
 * Tab-selected via NodeSelection — no caret position to read).
 */
export interface DictationGhostChip {
  chipId: string;
  text: string;
  offset: number | null;
}

export const dictationGhostChipAtom = atom<DictationGhostChip | null>(null);

/**
 * In-flight dictation transcript scoped to a SuggestionStrip card.
 * Strip-mode parallel of dictationGhostChipAtom — when the rad presses
 * ` / Shift+` with a strip card focused, the live transcript renders
 * inside the focused card so the rad sees their words appear in place
 * (matching inline-mode chip ghost UX). SuggestionCard reads this atom
 * and renders the ghost when rowId matches its item id.
 *
 * `offset` is the input's selectionStart at gesture-start. Null falls
 * back to end-of-text append (no caret captured, e.g. Tab-focused card).
 */
export interface DictationGhostStrip {
  rowId: string;
  text: string;
  offset: number | null;
}

export const dictationGhostStripAtom = atom<DictationGhostStrip | null>(null);

/**
 * Project a ReportFocus down to the chipId the rad is on, or null.
 *
 * Both chip-focused (Tab landed on the field) and caret-in-chip (mouse
 * clicked into the inner text) carry a chipId — readers that mount the
 * card, jump the viewer, gate hotkeys, or hard-target dictation treat
 * them identically. Keeping the projection in one place means a future
 * fifth mode that also carries a chipId only has to be added here.
 */
export const getFocusedChipId = (focus: ReportFocus): string | null =>
  focus.mode === "chip-focused" || focus.mode === "caret-in-chip" ? focus.chipId : null;

/**
 * Which chip (if any) has its InlineChipCard explicitly opened.
 *
 * Focus alone no longer mounts the card — Tab nav and plain clicks land
 * on a chip without opening the AI-suggestion picker so the card never
 * occludes the findings list below. The card renders only when this
 * atom's value matches `getFocusedChipId(reportFocusAtom)`.
 *
 * Writers:
 *   - PrototypeReport.openEditAttrsForChip (the chip right-click menu's
 *     "Edit attributes" item and the E shortcut) sets to chipId. There is
 *     no modifier-click opener since #549.
 *   - ChipView.handleInnerMouseDown: any plain click clears (also acts as
 *     a dismiss when re-clicking the same chip).
 *
 * No active clear is needed when a chip is removed or focus leaves the
 * editor — the comparison gate (id === focusedChipId) keeps the card
 * hidden as soon as the focused chip stops matching the stored id.
 */
export const inlineCardOpenChipIdAtom = atom<string | null>(null);
